import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Spin } from 'antd';
import { persistDashboardLayoutVariant } from '../helpers/dashboardLayoutVariant';
import { useCurrentUser } from '../contexts/UserContext';
import opportunityInfoService from '../services/oneButtonServices/opportunityInfoService';
import { useOneButtonStore } from '../store/oneButtonStore';
import { useViewerStore } from '../store/store';
import Unauthorized from '../pages/Unauthorized/Unauthorized';
import { PreContractIntake } from './components/PreContractIntake';
import {
    loadSiteContext,
    persistSiteContext,
    siteContextFromOpportunity,
    siteRowToContext,
    type PreContractSiteContext,
} from './lib/preContractTypes';
import { opportunityNumericId } from './lib/preContractTitle';
import type { SiteRow } from '../@types/types';
import { fetchAndStoreEnergyCommunityStatus } from './lib/preContractOpportunityPrefill';
import './styles/pre-contract-intake.css';

type LocationState = {
    site?: SiteRow;
};

function seedSiteContext(sfid: string, locationState: unknown): PreContractSiteContext | null {
    const state = locationState as LocationState | null;
    if (state?.site && (!sfid || state.site.salesForceId === sfid)) {
        return siteRowToContext(state.site);
    }
    if (sfid) {
        return loadSiteContext(sfid);
    }
    return null;
}

const PreContractPage: React.FC = () => {
    const { hasCapability, user } = useCurrentUser();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const sfid = searchParams.get('sfid')?.trim() ?? '';

    const seeded = useMemo(
        () => seedSiteContext(sfid, location.state),
        [location.state, sfid],
    );
    const [siteContext, setSiteContext] = useState<PreContractSiteContext | null>(seeded);
    const [loadingOpportunity, setLoadingOpportunity] = useState(() => Boolean(sfid) && !seeded);
    const [loadError, setLoadError] = useState(false);

    useEffect(() => {
        setSiteContext(seeded);
        setLoadError(false);
        setLoadingOpportunity(Boolean(sfid) && !seeded);
    }, [seeded, sfid]);

    useEffect(() => {
        document.title = 'OneDraw · Pre-contract intake';
        if (siteContext) {
            persistSiteContext(siteContext);
        }
    }, [siteContext]);

    useEffect(() => {
        if (!sfid) return undefined;

        let cancelled = false;
        persistDashboardLayoutVariant('v2');
        useViewerStore.setState({ dashboardLayoutVariant: 'v2' });
        useOneButtonStore.getState().setSalesForceId(sfid);

        void (async () => {
            if (!seeded) setLoadingOpportunity(true);
            const detailPromise = opportunityInfoService.getOpportunityDetail(sfid);
            const energyCommunityPromise = fetchAndStoreEnergyCommunityStatus(sfid);
            const detailRaw = await detailPromise;
            await energyCommunityPromise;
            if (cancelled) return;

            const opportunity = detailRaw != null ? (detailRaw?.data ?? detailRaw) : null;
            if (!opportunity) {
                if (!seeded) {
                    setLoadError(true);
                    setLoadingOpportunity(false);
                }
                return;
            }

            const next = siteContextFromOpportunity(sfid, opportunity);
            const store = useOneButtonStore.getState();
            store.setOpportunityDetails(opportunity);
            store.setOpportunityID(opportunityNumericId(opportunity));
            persistSiteContext(next);
            try {
                sessionStorage.setItem('opportunityName', next.opportunityName);
                if (next.stage) sessionStorage.setItem('stageStatus', next.stage);
            } catch {
                /* ignore */
            }
            setSiteContext(next);
            setLoadError(false);
            setLoadingOpportunity(false);
        })();

        return () => {
            cancelled = true;
        };
    }, [sfid, seeded]);

    if (user == null) {
        return (
            <div className="app__unauthorized" style={{ background: 'transparent' }}>
                <Spin size="default" />
                Fetching user data...
            </div>
        );
    }

    if (!hasCapability('Sites_Table') || user.application_role_name !== 'Admin') {
        return <Unauthorized />;
    }

    if (!sfid) {
        return (
            <div className="app__pre-contract app__pre-contract--centered">
                <div className="pci-error">
                    <h2>Opportunity not found</h2>
                    <p>
                        Open pre-contract intake with an opportunity URL, for example{' '}
                        <code>/pre-contract?sfid=006...</code>
                    </p>
                    <Button type="primary" onClick={() => navigate('/sites')}>
                        Back to sites
                    </Button>
                </div>
            </div>
        );
    }

    if (loadingOpportunity && !siteContext) {
        return (
            <div className="app__pre-contract app__pre-contract--centered">
                <div className="pci-error" role="status">
                    <Spin size="default" />
                    <p>Loading opportunity…</p>
                </div>
            </div>
        );
    }

    if (!siteContext || loadError) {
        return (
            <div className="app__pre-contract app__pre-contract--centered">
                <div className="pci-error">
                    <h2>Opportunity not found</h2>
                    <p>We could not load this Salesforce ID. Check the URL or open the site from the sites table.</p>
                    <Button type="primary" onClick={() => navigate('/sites')}>
                        Back to sites
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="app__pre-contract">
            <PreContractIntake key={siteContext.salesForceId} site={siteContext} userName={user.user_name} />
        </div>
    );
};

export default PreContractPage;
